import type { ReactNode } from "react";

// Lightweight table primitives. The outer wrapper carries the portal `.card`
// surface (rounded + bordered + shadow) so a `<Table>` drops cleanly into a
// page without any extra wrapping markup. Header cells use the shared
// `.table-head` rule for the uppercase eyebrow band.
type Props = {
  children: ReactNode;
  className?: string;
};

export function Table({ children, className = "" }: Props) {
  return (
    <div className={`card overflow-x-auto ${className}`.trim()}>
      <table className="min-w-full text-sm">{children}</table>
    </div>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return <thead>{children}</thead>;
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody>{children}</tbody>;
}

type ThProps = {
  children: ReactNode;
  className?: string;
  sortable?: boolean;
  sorted?: "asc" | "desc" | null;
  onSort?: () => void;
};

export function Th({ children, className = "", sortable, sorted, onSort }: ThProps) {
  if (sortable) {
    const indicator =
      sorted === "asc" ? "▲" : sorted === "desc" ? "▼" : "↕";
    return (
      <th scope="col" className={`table-head text-left ${className}`.trim()}>
        <button
          type="button"
          onClick={onSort}
          className="inline-flex items-center gap-1 transition hover:text-ink"
        >
          <span>{children}</span>
          <span aria-hidden="true" className="text-ink-soft/60">
            {indicator}
          </span>
        </button>
      </th>
    );
  }
  return (
    <th scope="col" className={`table-head text-left ${className}`.trim()}>
      {children}
    </th>
  );
}

export function Td({
  children,
  className = "",
  colSpan,
}: {
  children: ReactNode;
  className?: string;
  colSpan?: number;
}) {
  return (
    <td
      colSpan={colSpan}
      className={`px-4 py-3 align-top text-sm text-ink ${className}`.trim()}
    >
      {children}
    </td>
  );
}

export function Tr({
  children,
  className = "",
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <tr
      className={`border-b border-line transition last:border-b-0 ${
        onClick ? "cursor-pointer hover:bg-bg" : "hover:bg-bg/60"
      } ${className}`.trim()}
      onClick={onClick}
    >
      {children}
    </tr>
  );
}
